// Guide PDF Generator — personalized cruise/trip guides
// Uses pdf-lib (lazy-loaded) to overlay family name on cover template, then merge with guide content

import { store } from './store.js'

// Template paths (served from public/guides/)
const GUIDE_TEMPLATES = {
    crucero: {
        cover: '/guides/cruise-cover.pdf',
        guide: '/guides/cruise-guide.pdf',
        label: 'Crucero',
        downloadName: 'Disney Cruise'
    }
    // Future: disney, universal, parques_crucero, etc.
}

// Welcome PDFs by departure port
const WELCOME_TEMPLATES = {
    default: '/guides/cruise-welcome-default.pdf',
    ftl: '/guides/cruise-welcome-ftl.pdf',
    alaska: '/guides/cruise-welcome-alaska.pdf'
}

function getWelcomeTemplate(departurePort) {
    const port = (departurePort || '').toLowerCase()
    if (port.includes('lauderdale') || port.includes('ftl') || port.includes('miami')) return WELCOME_TEMPLATES.ftl
    if (port.includes('alaska') || port.includes('juneau') || port.includes('ketchikan') || port.includes('skagway') || port.includes('vancouver')) return WELCOME_TEMPLATES.alaska
    return WELCOME_TEMPLATES.default
}

async function generateGuide(type, familyName, leadId) {
    const template = GUIDE_TEMPLATES[type] || GUIDE_TEMPLATES.crucero
    window.toast('Generando guía...', 'success')

    try {
        // Lazy-load pdf-lib only when needed (~400KB, not in main bundle)
        const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib')

        // Fetch template PDFs + custom font in parallel
        const [coverBytes, guideBytes, fontBytes] = await Promise.all([
            fetch(template.cover).then(r => r.arrayBuffer()),
            fetch(template.guide).then(r => r.arrayBuffer()),
            fetch('/guides/PlayfairDisplay-Bold.ttf').then(r => r.arrayBuffer())
        ])

        // Load PDFs
        const coverDoc = await PDFDocument.load(coverBytes)
        const guideDoc = await PDFDocument.load(guideBytes)

        // Create final document
        const finalDoc = await PDFDocument.create()

        // Register fontkit for custom font embedding
        const fontkit = (await import('@pdf-lib/fontkit')).default
        finalDoc.registerFontkit(fontkit)

        // Copy cover page
        const [coverPage] = await finalDoc.copyPages(coverDoc, [0])

        // Embed Playfair Display Bold — closest match to TheSeasons-Bd used in cover title
        const font = await finalDoc.embedFont(fontBytes)
        const text = `FAMILIA ${familyName.toUpperCase()}`

        // A4: 595.5 x 842.25 pts
        // Position: centered, between "GUIA MÁGICA" and "Que comience su viaje inolvidable"
        const pageWidth = coverPage.getWidth()
        let fontSize = 26
        let textWidth = font.widthOfTextAtSize(text, fontSize)

        // Auto-shrink if name is too long
        while (textWidth > pageWidth - 80 && fontSize > 14) {
            fontSize -= 1
            textWidth = font.widthOfTextAtSize(text, fontSize)
        }

        const x = (pageWidth - textWidth) / 2
        const y = 480 // from bottom — centered between "GUÍA MÁGICA" (~513) and "Que comience..." (~447)

        coverPage.drawText(text, {
            x,
            y,
            size: fontSize,
            font,
            color: rgb(0.2, 0.2, 0.2) // dark charcoal
        })

        finalDoc.addPage(coverPage)

        // Copy all guide pages
        const guidePageIndices = Array.from({ length: guideDoc.getPageCount() }, (_, i) => i)
        const guidePages = await finalDoc.copyPages(guideDoc, guidePageIndices)
        guidePages.forEach(page => finalDoc.addPage(page))

        // Save and download
        const pdfBytes = await finalDoc.save()
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = `Guia Magica ${template.downloadName} - Familia ${familyName}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        window.toast('Guía descargada', 'success')

        // Auto-upload to dashboard if exists
        if (leadId && window.autoUploadToDashboard) {
            const filename = `Guia Magica ${template.downloadName} - Familia ${familyName}.pdf`
            window.autoUploadToDashboard(leadId, blob, filename, 'Guía Mágica')
        }
    } catch (err) {
        console.error('Guide PDF error:', err)
        window.toast('Error generando la guía: ' + err.message, 'error')
    }
}

async function downloadWelcome(leadId) {
    const lead = store.leads.find(l => l.id === leadId)
    if (!lead) return

    // Find cruise entry with port and reservation number
    const cruise = lead.cruises?.find(c => c.departurePort || c.reservationNum)
        || lead.formData?.cruises?.find(c => c.departurePort || c.reservationNum)
    const port = cruise?.departurePort || ''
    const reservationNum = cruise?.reservationNum || lead.reservationNum || ''
    const templateUrl = getWelcomeTemplate(port)
    // Spanish port labels for filename
    const portLower = (port || '').toLowerCase()
    let portLabel = 'Disney Cruise'
    if (portLower.includes('canaveral')) portLabel = 'Puerto Canaveral'
    else if (portLower.includes('lauderdale') || portLower.includes('ftl')) portLabel = 'Fort Lauderdale'
    else if (portLower.includes('miami')) portLabel = 'Fort Lauderdale'
    else if (portLower.includes('alaska') || portLower.includes('juneau') || portLower.includes('vancouver')) portLabel = 'Alaska'
    else if (port) portLabel = port

    window.toast('Generando Welcome...', 'success')

    try {
        const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib')
        const templateBytes = await fetch(templateUrl).then(r => r.arrayBuffer())
        const doc = await PDFDocument.load(templateBytes)
        const page = doc.getPages()[0]

        // Stamp reservation number after "Recuerda tu número de reserva:"
        // Templates have blank space after the colon — same font style as surrounding text (11pt regular)
        if (reservationNum) {
            const font = await doc.embedFont(StandardFonts.TimesRomanBold)
            page.drawText(reservationNum, {
                x: 232, y: 675, size: 11, font, color: rgb(0.13, 0.13, 0.13)
            })
        }

        const pdfBytes = await doc.save()
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const resLabel = reservationNum ? ` - ${reservationNum}` : ''
        a.download = `Welcome - ${portLabel}${resLabel}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        window.toast('Welcome descargado', 'success')

        // Auto-upload to dashboard if exists
        if (leadId && window.autoUploadToDashboard) {
            const filename = `Welcome - ${portLabel}${resLabel}.pdf`
            window.autoUploadToDashboard(leadId, blob, filename, 'Bienvenida')
        }
    } catch (err) {
        console.error('Welcome PDF error:', err)
        window.toast('Error generando Welcome: ' + err.message, 'error')
    }
}

function showGuideModal(leadId) {
    const lead = store.leads.find(l => l.id === leadId)
    if (!lead) return

    // Pre-fill: titular's second word (first surname)
    const words = (lead.titular || '').trim().split(/\s+/)
    const defaultName = words.length > 1 ? words.slice(1).join(' ') : words[0] || ''

    // Determine guide type
    const guideType = (lead.type === 'crucero' || lead.type === 'parques_crucero') ? 'crucero' : null
    if (!guideType) return

    // Build modal
    let modal = document.getElementById('guideDownloadModal')
    if (!modal) {
        modal = document.createElement('div')
        modal.id = 'guideDownloadModal'
        document.body.appendChild(modal)
    }

    modal.style.cssText = 'position:fixed;inset:0;z-index:2000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.5);'
    modal.innerHTML = `
        <div style="background:var(--bg-1);border:1px solid var(--border);border-radius:16px;padding:28px;width:380px;max-width:90vw;">
            <h3 style="margin:0 0 16px;font-size:16px;color:var(--text-1);">Descargar Guía ${GUIDE_TEMPLATES[guideType]?.label || ''}</h3>
            <label style="font-size:12px;color:var(--text-3);display:block;margin-bottom:4px;">Nombre de la familia (aparece en la portada)</label>
            <input id="guideFamilyName" type="text" value="${window.escHtml(defaultName)}"
                style="width:100%;padding:10px 12px;border:1px solid var(--border);border-radius:8px;background:var(--bg-2);color:var(--text-1);font-size:15px;margin-bottom:16px;box-sizing:border-box;"
                placeholder="Ej: Reyes Achury" />
            <div style="display:flex;gap:8px;justify-content:flex-end;">
                <button class="btn btn-ghost btn-sm" onclick="document.getElementById('guideDownloadModal').style.display='none'">Cancelar</button>
                <button class="btn btn-primary btn-sm" id="guideGenBtn" onclick="window._generateGuideFromModal('${guideType}','${leadId}')">Generar y Descargar</button>
            </div>
        </div>
    `

    // Focus input and select text
    setTimeout(() => {
        const input = document.getElementById('guideFamilyName')
        if (input) { input.focus(); input.select() }
    }, 100)
}

function _generateGuideFromModal(type, leadId) {
    const name = document.getElementById('guideFamilyName')?.value?.trim()
    if (!name) { window.toast('Ingresa el nombre de la familia', 'error'); return }

    const btn = document.getElementById('guideGenBtn')
    if (btn) { btn.disabled = true; btn.textContent = 'Generando...' }

    document.getElementById('guideDownloadModal').style.display = 'none'
    generateGuide(type, name, leadId)
}

Object.assign(window, { showGuideModal, _generateGuideFromModal, downloadWelcome })
